import { EndpointSpec, toZodError } from "api"
import { Mutex } from "open-lib"
import { logError } from "./logError"

export function readObjectProperty(object: unknown, property: string): unknown {
	if (typeof object == "object" && object != null && property in object) {
		return object[property]
	}
}

/**
 * Makes a request to the server, returns null if an error is detected
 * @param spec
 * @param args
 * @param abortSignal
 * @returns
 */
export async function requestApi2<I, O>(
	spec: EndpointSpec<I, O>,
	args?: {} & I,
	abortSignal?: AbortSignal
): Promise<O | null> {
	try {
		return await requestApi2_getError(spec, args, abortSignal)
	} catch (err) {
		logError(err)
	}
	return null
}

export async function requestApi2_getError<I, O>(
	spec: EndpointSpec<I, O>,
	args?: {} & I,
	abortSignal?: AbortSignal
): Promise<O | null> {
	if (spec.getName() == undefined) {
		throw new Error(`Internal error: endpoint name is missing`)
	}
	const url = spec.getName()
	if (spec.resultParser == null) {
		await requestUrl(url, args, abortSignal)
		return null
	}
	const rawData = await requestUrlGetJson(url, args, abortSignal)
	try {
		return spec.resultParser.parse(rawData)
	} catch (err) {
		throw new Error(`Failed to validate returned data from endpoint: ${spec.getName()}`, {
			cause: toZodError(err) ?? err,
		})
	}
}

export async function requestUrl(
	url: string,
	args?: unknown,
	abortSignal?: AbortSignal
): Promise<Response> {
	const response = await fetch(url, {
		signal: abortSignal,
		method: "POST",
		mode: "cors",
		cache: "no-cache",
		credentials: "same-origin",
		headers: { "content-type": "application/json" },
		body: JSON.stringify(args ?? {}),
	})
	if (!response.ok) {
		let text = await response.text()
		if (text == "") {
			text = `A request failed: ${url}\nStatus: ${response.statusText}`
		}
		throw new Error(text)
	}
	return response
}

let allowParalellRequests = true
// created by AI: Unit tests import this module in Node where window does not exist.
if (typeof window !== "undefined" && window.location.search.slice(1).split("&").includes("series")) {
	console.log("Warning! Paralell HTTP requests have been disabled.")
	allowParalellRequests = false
}
const mutex = new Mutex()

export async function requestUrlGetJson(
	url: string,
	args?: unknown,
	abortSignal?: AbortSignal
): Promise<unknown> {
	let response: Response
	if (allowParalellRequests) {
		response = await requestUrl(url, args, abortSignal)
	} else {
		response = await mutex.runExclusive(() => requestUrl(url, args, abortSignal))
	}
	return await response.json()
}
